// sitescribe-pwa/queue.ts
//
// IndexedDB-backed queue for diary submissions that need to retry
// (offline at submit time, or transient network failures).
//
// This module is used by App.tsx (online-event flush path).
// The service worker uses RAW IndexedDB directly (it can't import modules),
// but the schema is identical — see service-worker.js for the mirror.
//
// Schema:
//   Database: "sitescribe"   (version 1)
//   Store:    "queue"        (keyPath: "id")
//
// IMPORTANT: the queued item's `id` is also used as the `session_id`
// form field when POSTing /api/diary/submit. This means a retry hits
// the same diary directory on the backend, so retries are idempotent
// on disk. Without this, every retry would create a new diary.

import { openDB, type IDBPDatabase } from 'idb';

export const DB_NAME = 'sitescribe';
export const STORE_NAME = 'queue';
export const DB_VERSION = 1;

export interface QueuedFile {
  field: string;       // form field name, e.g. "audio_q1" or "photo_q1_0"
  blob: Blob;
  filename: string;
}

export interface QueuedDiary {
  id: string;                              // client-generated uuid; also used as session_id
  createdAt: number;                       // epoch ms
  attempts: number;
  lastError: string | null;
  lastAttemptAt: number | null;            // epoch ms or null
  state: 'queued' | 'sending' | 'failed';
  fields: Record<string, string>;          // text form fields
  files: QueuedFile[];
  summary: { project: string; date: string };  // for UI display
}

let _dbPromise: Promise<IDBPDatabase> | null = null;

function getDB(): Promise<IDBPDatabase> {
  if (!_dbPromise) {
    _dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      },
    });
  }
  return _dbPromise;
}

export async function enqueueDiary(item: QueuedDiary): Promise<void> {
  const db = await getDB();
  await db.put(STORE_NAME, item);
}

export async function listQueue(): Promise<QueuedDiary[]> {
  const db = await getDB();
  const items = await db.getAll(STORE_NAME);
  // Oldest first
  items.sort((a, b) => a.createdAt - b.createdAt);
  return items;
}

export async function getQueueCount(): Promise<number> {
  const db = await getDB();
  return db.count(STORE_NAME);
}

export async function removeFromQueue(id: string): Promise<void> {
  const db = await getDB();
  await db.delete(STORE_NAME, id);
}

export async function markAttempting(id: string): Promise<void> {
  const db = await getDB();
  const item = await db.get(STORE_NAME, id);
  if (!item) return;
  item.state = 'sending';
  item.attempts = (item.attempts || 0) + 1;
  item.lastAttemptAt = Date.now();
  await db.put(STORE_NAME, item);
}

export async function markFailed(id: string, error: string): Promise<void> {
  const db = await getDB();
  const item = await db.get(STORE_NAME, id);
  if (!item) return;
  item.state = 'failed';
  item.lastError = error;
  item.lastAttemptAt = Date.now();
  await db.put(STORE_NAME, item);
}

/**
 * Reconstruct a FormData object suitable for POST /api/diary/submit
 * from a queued item. Includes session_id so the backend treats retries
 * as idempotent updates of the same diary directory.
 */
export function buildFormData(item: QueuedDiary): FormData {
  const fd = new FormData();
  fd.append('session_id', item.id);
  for (const [k, v] of Object.entries(item.fields)) {
    fd.append(k, v);
  }
  for (const f of item.files) {
    fd.append(f.field, f.blob, f.filename);
  }
  return fd;
}

/**
 * Generate a client-side session id. Uses crypto.randomUUID where available
 * (all modern browsers), falls back to a timestamp+random string.
 */
export function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}
